import { useState } from "react";
import { Radio, Square, Volume2 } from "lucide-react";
import type { SimState } from "../domain/model";
import type { Perform } from "./ResourceManager";
import type { RadioController } from "../audio/useRadioAudio";
import { callsign, speechReasonLabels } from "../domain/radio";

export function sortedSpeechRequests(state: SimState) {
  return [...state.speechRequests].sort(
    (a, b) =>
      Number(b.phase === "reported") - Number(a.phase === "reported") ||
      Number(b.urgent) - Number(a.urgent) ||
      a.createdAt - b.createdAt,
  );
}

export default function RadioDock({
  state,
  act,
  enabled,
  audio,
  onIncident,
}: {
  state: SimState;
  act: Perform;
  enabled: boolean;
  audio: RadioController;
  onIncident: (id: string) => void;
}) {
  const [chosen, setChosen] = useState<string>();
  const requests = sortedSpeechRequests(state);
  const conversation = requests.find((r) => r.phase === "reported");
  const current =
    conversation ?? requests.find((r) => r.id === chosen) ?? requests[0];
  const unit = state.vehicles.find((v) => v.id === current?.vehicleId);
  const incident = state.incidents.find((i) => i.id === current?.incidentId);
  const r = audio.runtime,
    p = audio.preferences;
  const direct = p.playback === "direct";
  const audioState =
    r.directPhase === "blocked" && direct
      ? "▶ drücken: Browser wartet auf Freigabe"
      : !p.enabled
        ? "Funk stumm"
        : !p.speech
          ? "Sprache aus"
          : !p.volume
            ? "Lautstärke 0 %"
            : r.callerSpeaking
              ? `Telefon spricht · Funk wartet${r.queued ? ` · ${r.queued} Meldung(en)` : ""}`
              : direct && r.directPhase === "playing"
                ? "Direktton · " + r.directSeconds.toFixed(1) + " s"
                : direct &&
                    r.directPhase === "paused" &&
                    r.speaking &&
                    r.currentVoice
                  ? "Aufnahme pausiert · ▶ zum Fortsetzen"
                  : r.speaking
                    ? r.currentVoice || "Sprachaufnahme wird geladen …"
                    : r.unlocked
                      ? "Funk bereit"
                      : "Ton aktivieren";
  return (
    <section className="radio-dock" aria-label="Sprechwünsche und Funk unten">
      <header className="radio-dock-heading">
        <Radio size={17} />
        <b>Sprechwünsche</b>
        <span className="count-badge">{requests.length} offen</span>
        {!requests.length && (
          <span className="radio-dock-empty">Kein Sprechwunsch offen</span>
        )}
        {requests.length > 0 && (
          <div className="radio-dock-tabs" aria-label="Offene Sprechwünsche">
            {requests.map((item) => {
              const v = state.vehicles.find((v) => v.id === item.vehicleId);
              return (
                <button
                  key={item.id}
                  aria-pressed={item.id === current?.id}
                  disabled={!!conversation && item.id !== conversation.id}
                  onClick={() => setChosen(item.id)}
                  className={item.urgent ? "urgent" : ""}
                >
                  {item.phase === "reported"
                    ? "Rückmeldung"
                    : item.urgent
                      ? "Dringend · S5"
                      : "S5"}{" "}
                  · {v ? callsign(v) : "Fahrzeug"}
                </button>
              );
            })}
          </div>
        )}
      </header>
      {current && unit && incident && (
        <div className="radio-dock-request" key={current.id}>
          <div className="radio-dock-unit">
            <b>{callsign(unit)}</b>
            <button
              className="text-button"
              onClick={() => onIncident(incident.id)}
            >
              E {incident.number} · {incident.address}
            </button>
            <small>
              {speechReasonLabels[current.reason]} · Status {unit.status}
            </small>
          </div>
          <p className="radio-dock-report">
            {current.phase === "reported"
              ? current.report
              : "Sprechwunsch liegt vor. Mit „Kommen“ die Rückmeldung freigeben."}
          </p>
          <button
            className="button primary"
            disabled={
              !enabled || (current.phase === "pending" && !!conversation)
            }
            onClick={() =>
              void act(
                {
                  type:
                    current.phase === "pending"
                      ? "grantSpeech"
                      : "acknowledgeSpeech",
                  requestId: current.id,
                },
                current.phase === "pending"
                  ? "Sprechfreigabe erteilt."
                  : "Rückmeldung bestätigt.",
              )
            }
          >
            {current.phase === "pending" ? "J · Kommen" : "Verstanden · Ende"}
          </button>
        </div>
      )}
      <div className="radio-dock-audio">
        <button
          className="button secondary"
          onClick={() => void audio.test("direct")}
        >
          <Volume2 size={15} />
          Direktton testen
        </button>
        <audio
          ref={audio.directRef}
          controls
          preload="auto"
          aria-label="Direkte deutsche Funkstimme"
          className={direct ? "" : "inactive-player"}
        />
        <span className="radio-dock-output" role="status">
          {audioState}
          <small>
            {r.currentVoice ||
              (direct
                ? "Neuronale Sprache · direkter Audioausgang"
                : "Neuronale Sprache · Funkfilter")}
          </small>
        </span>
        <button
          className="button secondary"
          aria-label="Funkwiedergabe stoppen"
          onClick={audio.stop}
        >
          <Square size={14} />
        </button>
      </div>
      {r.error && (
        <p className="radio-dock-error" role="alert">
          {r.error}
        </p>
      )}
    </section>
  );
}
